import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  FetchModelBlocks,
  getDashboard,
  getDashboardBlockStatus,
  getFetchDashboardStatus,
  initializeModelDashboards,
  resetDashboardSliceV2,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';

export interface UseDashboardDataOptions {
  /** Fetch builder blocks/dimensions needed by dashboard instruments. */
  loadBlocks?: boolean;
  /**
   * List packs for the model and load the selected pack when none is in Redux yet.
   * No-ops once `dashboard.id` is set (e.g. header URL bootstrap finished first).
   */
  bootstrapPack?: boolean;
}

/**
 * Dashboard tab data bootstrap: builder blocks and (optionally) multi-dashboard pack load.
 * Mirrors Dashboard-v2's block loader so the dashboard tab does not depend on NavigationBar
 * being mounted (e.g. mobile menu closed).
 */
const useDashboardData = ({
  loadBlocks = true,
  bootstrapPack = false,
}: UseDashboardDataOptions = {}) => {
  const model = useSelector(getBlocks);
  const dashboard = useSelector(getDashboard);
  const dashboardBlockStatus = useSelector(getDashboardBlockStatus);
  const fetchDashboardStatus = useSelector(getFetchDashboardStatus);
  const dispatch: AppDispatch = useDispatch();
  const packBootstrapKeyReference = useRef<string | null>(null);

  const hookActive = loadBlocks || bootstrapPack;

  useEffect(() => {
    packBootstrapKeyReference.current = null;
  }, [model?.id]);

  useEffect(() => {
    return () => {
      if (hookActive && dashboard?.id) {
        dispatch(resetDashboardSliceV2());
      }
    };
  }, [dispatch, hookActive, dashboard?.id]);

  useEffect(() => {
    if (model?.id && loadBlocks && dashboardBlockStatus === STATUS.IDLE) {
      dispatch(FetchModelBlocks({ modelId: Number(model.id) }));
    }
  }, [model?.id, loadBlocks, dashboardBlockStatus, dispatch]);

  useEffect(() => {
    if (!bootstrapPack || !model?.id || dashboard?.id) {
      return;
    }
    if (fetchDashboardStatus === STATUS.LOADING) {
      return;
    }

    const bootstrapKey = String(model.id);
    if (
      packBootstrapKeyReference.current === bootstrapKey &&
      fetchDashboardStatus !== STATUS.FAILED
    ) {
      return;
    }

    packBootstrapKeyReference.current = bootstrapKey;
    dispatch(initializeModelDashboards({ model }));
  }, [bootstrapPack, dashboard?.id, dispatch, fetchDashboardStatus, model]);
};

export default useDashboardData;
